import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { ConversationId, Cursor, Message, Page, SavedMessage } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS } from '../constants';
import { patchMessage } from '../lib/timelineCache';

interface UseSavedMessagesResult {
  saved: SavedMessage[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
}

/** The personal, cross-conversation Saved queue. */
export function useSavedMessages(enabled = true): UseSavedMessagesResult {
  const query = useInfiniteQuery<
    Page<SavedMessage>,
    Error,
    Page<SavedMessage>[],
    readonly unknown[],
    Cursor | undefined
  >({
    queryKey: CHAT_KEYS.saved(),
    initialPageParam: undefined,
    enabled,
    queryFn: ({ pageParam }) => chatApi.listSavedMessages(pageParam),
    getNextPageParam: (last) =>
      last.pageInfo.hasNextPage ? (last.pageInfo.endCursor ?? undefined) : undefined,
    select: (data) => data.pages,
    staleTime: 30_000,
  });

  const saved = useMemo(() => (query.data ?? []).flatMap((page) => page.items), [query.data]);

  return {
    saved,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

/**
 * Star / unstar, optimistic on the bubble and lazy on the list.
 *
 * The timeline patch is immediate because the star is the user's own feedback;
 * the Saved LIST is merely invalidated, because it is usually not on screen and
 * refetching it eagerly would be work nobody asked for.
 */
export function useToggleSaved(
  conversationId: ConversationId | null,
): (message: Message) => void {
  const client = useQueryClient();

  return useCallback(
    (message: Message) => {
      if (!conversationId) return;
      const next = !message.isSaved;
      patchMessage(client, conversationId, message.id, (current) => ({ ...current, isSaved: next }));

      const call = next
        ? chatApi.saveMessage(conversationId, message.id)
        : chatApi.unsaveMessage(conversationId, message.id);

      void call
        .then(() => client.invalidateQueries({ queryKey: CHAT_KEYS.saved() }))
        .catch(() => {
          patchMessage(client, conversationId, message.id, (current) => ({
            ...current,
            isSaved: !next,
          }));
          toast.error(next ? 'Could not save that message.' : 'Could not remove that.');
        });
    },
    [client, conversationId],
  );
}
